<template>
  <div class="p-m-3">
    <h2>
      Branch &amp; Product Analysis
      <span class="bpa-draft" title="A working draft on live data, for review">DRAFT</span>
    </h2>

    <div v-if="permissionsPending">
      <ProgressSpinner style="width: 30px; height: 30px;" strokeWidth="6" />
    </div>
    <div v-else-if="!canView">
      <h3>Not authorised</h3>
    </div>
    <div v-else>
      <!-- The pill bar. Every click re-runs one aggregate query, debounced. -->
      <div class="p-card p-p-3 p-mb-3">
        <div v-for="dim in dimensions" :key="dim.key" class="bpa-pill-row">
          <span class="bpa-pill-label">{{ dim.label }}</span>
          <div class="bpa-pills">
            <Button
              label="All"
              :class="pillClass(isAllSelected(dim.key))"
              @click="onToggleAll(dim.key)"
            />
            <Button
              v-for="o in options[dim.key]"
              :key="o.id"
              :label="o.code && dim.key === 'branches' ? o.code : o.name"
              :title="o.name"
              :class="pillClass(selection[dim.key].indexOf(o.id) !== -1)"
              @click="onTogglePill(dim.key, o.id)"
            />
          </div>
        </div>

        <div class="bpa-pill-row">
          <span class="bpa-pill-label">Received</span>
          <div class="bpa-pills">
            <Button
              v-for="p in periods"
              :key="p.key"
              :label="p.label"
              :class="pillClass(period === p.key)"
              @click="period = p.key"
            />
            <template v-if="period === 'custom'">
              <Calendar v-model="customFrom" dateFormat="dd/mm/yy" placeholder="From" class="bpa-date" :showIcon="true" />
              <Calendar v-model="customTo" dateFormat="dd/mm/yy" placeholder="To" class="bpa-date" :showIcon="true" />
            </template>
          </div>
        </div>

        <div class="p-d-flex p-ai-center p-flex-wrap p-mt-2" style="gap: 22px;">
          <div class="p-d-flex p-ai-center" style="gap: 8px;">
            <span :class="{ 'bpa-switch-on': basis === 'enquiry' }">Branch = enquiry's branch</span>
            <InputSwitch id="bpaBasis" v-model="creatorBasis" />
            <span :class="{ 'bpa-switch-on': basis === 'creator' }">
              creator's branch <small>(today's branch)</small>
            </span>
          </div>
          <div class="p-d-flex p-ai-center" style="gap: 8px;">
            <span :class="{ 'bpa-switch-on': measure === 'value' }">Value</span>
            <InputSwitch id="bpaMeasure" v-model="measureIsCount" />
            <span :class="{ 'bpa-switch-on': measure === 'count' }">Count</span>
          </div>
          <div class="p-d-flex p-ai-center" style="gap: 8px;">
            <Checkbox id="bpaHideEmpty" v-model="hideEmpty" :binary="true" />
            <label for="bpaHideEmpty" style="margin: 0;">Hide empty rows and columns</label>
          </div>
          <ProgressSpinner v-if="loading" style="width: 24px; height: 24px;" strokeWidth="7" />
          <span v-else-if="windowText" class="bpa-muted">{{ windowText }}</span>
        </div>
        <div v-if="errorMessage" class="p-mt-2" style="color: #c62828;">{{ errorMessage }}</div>
      </div>

      <!-- The grid. -->
      <div class="p-card p-p-3 p-mb-3">
        <div v-if="emptyReason" class="bpa-muted">{{ emptyReason }}</div>
        <div v-else class="bpa-grid-wrap">
          <table class="bpa-grid" :class="{ 'bpa-loading': loading }">
            <thead>
              <tr>
                <th class="bpa-corner">{{ basis === 'creator' ? "Creator's branch" : 'Branch' }} / Product type</th>
                <th v-for="c in cols" :key="'h' + c.id" :title="c.title">{{ c.label }}</th>
                <th class="bpa-total">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in rows" :key="'r' + r.id">
                <th :title="r.title">{{ r.label }}</th>
                <td
                  v-for="c in cols"
                  :key="r.id + '|' + c.id"
                  :class="cellClass(r, c)"
                  :style="heat(grid.cell(r.id, c.id))"
                  @click="openCell(r, c)"
                >
                  <template v-if="grid.cell(r.id, c.id)">
                    <div class="bpa-main">{{ mainText(grid.cell(r.id, c.id)) }}</div>
                    <div class="bpa-sub">{{ subText(grid.cell(r.id, c.id)) }}</div>
                  </template>
                </td>
                <td class="bpa-total">
                  <template v-if="grid.rowTotals[r.id].count">
                    <div class="bpa-main">{{ mainText(grid.rowTotals[r.id]) }}</div>
                    <div class="bpa-sub">{{ subText(grid.rowTotals[r.id]) }}</div>
                  </template>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th>Total</th>
                <td v-for="c in cols" :key="'t' + c.id" class="bpa-total">
                  <template v-if="grid.colTotals[c.id].count">
                    <div class="bpa-main">{{ mainText(grid.colTotals[c.id]) }}</div>
                    <div class="bpa-sub">{{ subText(grid.colTotals[c.id]) }}</div>
                  </template>
                </td>
                <td class="bpa-total bpa-grand">
                  <div class="bpa-main">{{ mainText(grid.grand) }}</div>
                  <div class="bpa-sub">{{ subText(grid.grand) }}</div>
                </td>
              </tr>
            </tfoot>
          </table>
          <small class="bpa-muted">
            Value is the active quote's value. Click a figure to list the enquiries behind it.
          </small>
        </div>
      </div>

      <!-- The enquiries behind the clicked cell. -->
      <div v-if="picked" ref="rowsCard" class="p-card p-p-3 p-mb-3">
        <div class="p-d-flex p-ai-center p-jc-between">
          <h3 style="margin: 0;">{{ picked.row.title }} &middot; {{ picked.col.title }}</h3>
          <Button icon="pi pi-times" class="p-button-text p-button-rounded" title="Close" @click="picked = null" />
        </div>
        <p v-if="rowsTruncated" class="bpa-muted">Showing the 500 largest.</p>
        <DataTable
          :value="cellRows"
          :loading="rowsLoading"
          :autoLayout="true"
          :paginator="cellRows.length > 25"
          :rows="25"
          dataKey="oppId"
          class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
        >
          <template #empty>No enquiries.</template>
          <Column field="quoteNo" header="Quote No" :sortable="true" />
          <Column field="custName" header="Customer" :sortable="true" />
          <Column field="description" header="Description" :sortable="true" />
          <Column field="value" header="Value" :sortable="true" bodyStyle="text-align: right; white-space: nowrap;">
            <template #body="s">{{ money(s.data.value) }}</template>
          </Column>
          <Column field="status" header="Status" :sortable="true" />
          <Column field="dateReceived" header="Received" :sortable="true" bodyStyle="white-space: nowrap;">
            <template #body="s">{{ ukDate(s.data.dateReceived) }}</template>
          </Column>
          <Column field="bookedBy" header="Booked By" :sortable="true" />
          <Column header="" bodyStyle="white-space: nowrap;">
            <template #body="s">
              <Button label="Open" icon="pi pi-external-link" class="p-button-text p-button-sm" @click="openEnquiry(s.data.oppId)" />
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import BranchProductAnalysisService from '@/service/BranchProductAnalysisService'
import { formatCurrency } from '@/utils/money'
import {
  NOT_SET, PERIODS, isAll, toggleAll, togglePill, dimensionParam, axis, buildGrid, isoDate, debounce
} from './branchProductAnalysis'

const PERMISSION = 'Dashboard-ComposeWidgets'
const DEBOUNCE_MS = 250

export default {
  name: 'BranchProductAnalysis',
  data() {
    return {
      options: { branches: [], productTypes: [], statuses: [] },
      selection: { branches: [], productTypes: [], statuses: [] },
      initialised: false,
      period: 'last12months',
      customFrom: null,
      customTo: null,
      creatorBasis: false,
      measureIsCount: false,
      hideEmpty: true,
      cells: [],
      window: null,
      loading: false,
      errorMessage: null,
      requestSeq: 0,
      picked: null,
      cellRows: [],
      rowsLoading: false,
      rowsTruncated: false,
      periods: PERIODS,
      dimensions: [
        { key: 'branches', label: 'Branches' },
        { key: 'productTypes', label: 'Product Types' },
        { key: 'statuses', label: 'Status' }
      ]
    }
  },
  computed: {
    myPermissions() {
      return this.$store.getters['auth/getUserPermissions'] || []
    },
    permissionsPending() {
      return this.myPermissions.length === 0
    },
    canView() {
      const p = this.myPermissions.find(a => a.permission && a.permission.permissionCode === PERMISSION)
      return !!(p && p.permissionLevel !== 0)
    },
    basis() {
      return this.creatorBasis ? 'creator' : 'enquiry'
    },
    measure() {
      return this.measureIsCount ? 'count' : 'value'
    },
    rows() {
      return axis(this.options.branches, this.selection.branches, this.cells, 'branchId', this.hideEmpty)
    },
    cols() {
      return axis(this.options.productTypes, this.selection.productTypes, this.cells, 'productTypeId', this.hideEmpty)
    },
    grid() {
      return buildGrid(this.cells, this.rows, this.cols)
    },
    maxCell() {
      let max = 0
      this.rows.forEach(r => this.cols.forEach(c => {
        const x = this.grid.cell(r.id, c.id)
        if (x) max = Math.max(max, this.measureOf(x))
      }))
      return max
    },
    emptyReason() {
      if (!this.initialised) return 'Loading...'
      const missing = this.dimensions.find(d => this.selection[d.key].length === 0)
      if (missing) return 'Pick at least one of ' + missing.label + '.'
      if (this.period === 'custom' && !this.customFrom && !this.customTo) return 'Pick a from or a to date.'
      if (!this.loading && this.rows.length === 0) return 'No enquiries match these filters.'
      return null
    },
    windowText() {
      if (!this.window) return ''
      const f = this.window.from ? this.ukDate(this.window.from) : 'the start'
      const t = this.window.to ? this.ukDate(this.window.to) : 'today'
      return 'Received ' + f + ' to ' + t
    }
  },
  watch: {
    canView(val) {
      if (val) this.load()
    },
    period() {
      this.scheduleLoad()
    },
    customFrom() {
      if (this.period === 'custom') this.scheduleLoad()
    },
    customTo() {
      if (this.period === 'custom') this.scheduleLoad()
    },
    creatorBasis() {
      this.scheduleLoad()
    }
  },
  created() {
    this.service = new BranchProductAnalysisService()
    this.scheduleLoad = debounce(() => this.load(), DEBOUNCE_MS)
  },
  mounted() {
    if (this.canView) this.load()
  },
  beforeDestroy() {
    this.scheduleLoad.cancel()
  },
  methods: {
    ids(key) {
      return this.options[key].map(o => o.id)
    },
    isAllSelected(key) {
      return isAll(this.selection[key], this.ids(key))
    },
    onToggleAll(key) {
      this.selection[key] = toggleAll(this.selection[key], this.ids(key))
      this.scheduleLoad()
    },
    onTogglePill(key, id) {
      this.selection[key] = togglePill(this.selection[key], id, this.ids(key))
      this.scheduleLoad()
    },
    pillClass(on) {
      return ['p-button-rounded', 'p-button-sm', 'bpa-pill', on ? '' : 'p-button-outlined']
    },
    params() {
      const p = {
        period: this.period,
        basis: this.basis
      }
      if (this.initialised) {
        p.branches = dimensionParam(this.selection.branches, this.ids('branches'))
        p.productTypes = dimensionParam(this.selection.productTypes, this.ids('productTypes'))
        p.statuses = dimensionParam(this.selection.statuses, this.ids('statuses'))
      }
      if (this.period === 'custom') {
        p.from = isoDate(this.customFrom)
        p.to = isoDate(this.customTo)
      }
      return p
    },
    async load() {
      // Nothing to ask for: the grid shows emptyReason instead.
      if (this.initialised && this.dimensions.some(d => this.selection[d.key].length === 0)) {
        this.requestSeq++
        this.cells = []
        this.picked = null
        return
      }
      if (this.period === 'custom' && !this.customFrom && !this.customTo) return
      const seq = ++this.requestSeq
      this.loading = true
      this.errorMessage = null
      try {
        // The first call has no options yet, so it cannot name a status; it brings the pills, and the
        // page then asks again for its default (Active).
        const params = this.params()
        const data = await this.service.getGrid(params)
        if (seq !== this.requestSeq) return
        this.options = Object.assign({ branches: [], productTypes: [], statuses: [] }, data.options || {})
        if (!this.initialised) {
          this.selection.branches = this.ids('branches')
          this.selection.productTypes = this.ids('productTypes')
          const active = this.options.statuses.filter(s => String(s.name).toLowerCase() === 'active')
          this.selection.statuses = active.length ? active.map(s => s.id) : this.ids('statuses')
          this.initialised = true
          // The first answer covered every status; ask again with the default status selection.
          if (active.length) {
            this.loading = false
            return this.load()
          }
        }
        this.cells = data.cells || []
        this.window = data.window || null
        this.picked = null
      } catch (e) {
        if (seq !== this.requestSeq) return
        this.errorMessage = 'Could not load the analysis. ' + ((e && e.message) || '')
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    },
    measureOf(x) {
      return this.measure === 'count' ? x.count || 0 : x.value || 0
    },
    money(v) {
      return v === null || v === undefined ? '' : formatCurrency(v)
    },
    mainText(x) {
      return this.measure === 'count' ? String(x.count) : this.money(x.value)
    },
    subText(x) {
      return this.measure === 'count' ? this.money(x.value) : x.count + (x.count === 1 ? ' enquiry' : ' enquiries')
    },
    heat(x) {
      if (!x || !this.maxCell) return null
      const share = this.measureOf(x) / this.maxCell
      return { backgroundColor: 'rgba(33, 150, 243, ' + (0.06 + 0.34 * share).toFixed(3) + ')' }
    },
    cellClass(r, c) {
      const on = this.picked && this.picked.row.id === r.id && this.picked.col.id === c.id
      return { 'bpa-cell': true, 'bpa-clickable': !!this.grid.cell(r.id, c.id), 'bpa-picked': on }
    },
    async openCell(row, col) {
      if (!this.grid.cell(row.id, col.id)) return
      this.picked = { row, col }
      this.cellRows = []
      this.rowsLoading = true
      this.rowsTruncated = false
      const picked = this.picked
      try {
        const data = await this.service.getRows(Object.assign(this.params(), {
          cellBranch: row.id === NOT_SET ? NOT_SET : row.id,
          cellProductType: col.id === NOT_SET ? NOT_SET : col.id
        }))
        if (this.picked !== picked) return
        this.cellRows = data.rows || []
        this.rowsTruncated = !!data.truncated
        this.$nextTick(() => {
          const el = this.$refs.rowsCard
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      } catch (e) {
        this.errorMessage = 'Could not load the enquiries. ' + ((e && e.message) || '')
      } finally {
        if (this.picked === picked) this.rowsLoading = false
      }
    },
    ukDate(iso) {
      if (!iso) return ''
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
      return m ? m[3] + '/' + m[2] + '/' + m[1] : iso
    },
    openEnquiry(oppId) {
      if (!oppId) return
      // The detail route's param is named :custid but carries the enquiry (opp) id. New tab, so the
      // grid stays where it is.
      window.open('/enquiries/' + oppId + '/detail', '_blank')
    }
  }
}
</script>

<style scoped>
.bpa-draft {
  font-size: 0.7rem;
  font-weight: 700;
  vertical-align: middle;
  padding: 2px 8px;
  border-radius: 10px;
  background: #fbc02d;
  color: #212529;
  margin-left: 8px;
}

.bpa-pill-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}

.bpa-pill-label {
  font-weight: 600;
  min-width: 7.5rem;
  padding-top: 6px;
}

.bpa-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.bpa-pills /deep/ .bpa-pill.p-button {
  padding: 0.3rem 0.8rem;
  font-size: 0.85rem;
}

.bpa-date {
  width: 11rem;
}

.bpa-switch-on {
  font-weight: 600;
}

.bpa-muted {
  color: #6c757d;
}

.bpa-grid-wrap {
  overflow-x: auto;
}

.bpa-grid {
  border-collapse: collapse;
  font-size: 0.85rem;
  margin-bottom: 8px;
}

.bpa-grid.bpa-loading {
  opacity: 0.55;
}

.bpa-grid th,
.bpa-grid td {
  border: 1px solid #dee2e6;
  padding: 6px 10px;
  white-space: nowrap;
}

.bpa-grid thead th {
  background: #f8f9fa;
  text-align: right;
  position: sticky;
  top: 0;
}

.bpa-grid thead th.bpa-corner,
.bpa-grid tbody th,
.bpa-grid tfoot th {
  text-align: left;
  background: #f8f9fa;
}

.bpa-grid td {
  text-align: right;
  min-width: 7rem;
}

.bpa-clickable {
  cursor: pointer;
}

.bpa-clickable:hover {
  outline: 2px solid #2196f3;
  outline-offset: -2px;
}

.bpa-picked {
  outline: 2px solid #1565c0;
  outline-offset: -2px;
}

.bpa-total {
  background: #f1f3f5;
  font-weight: 600;
}

.bpa-grand {
  background: #e3f2fd;
}

.bpa-main {
  font-weight: 600;
}

.bpa-sub {
  color: #6c757d;
  font-size: 0.75rem;
}
</style>
